import React, { useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import debounce from "lodash.debounce";
import Card from "components/Card";
import { Grid } from "components/Containers";
import { Label, Select, TextInput } from "components/Form";
import { AdvancedFilters } from "components/AdvancedFilters";
import { TIMELINE_LIST_FILTERS_CONFIG, DIRECTION, TIMELINE_LIST_SORTING_OPTIONS } from "constants/index";
import { toUcFirst } from "utils";

/**
 * TimelineListControls
 *
 * @param {Boolean}    hideAdvancedFilters
 * @param {Boolean}    hideKeywordSearch
 * @param {Boolean}    hideSorts
 * @param {Object}     selected
 * @param {Function}   setSelected
 */
const TimelineListControls = ({
  hideAdvancedFilters = false,
  hideKeywordSearch = false,
  hideSorts = false,
  selected,
  setSelected,
  loading,
  ...props
}) => {
  if (hideKeywordSearch && hideSorts && hideAdvancedFilters) return null;

  const [keyword, setKeyword] = useState(selected?.keyword);
  const debouncedSetKeyword = useMemo(
    () => debounce((val) => setSelected((prev) => ({ ...prev, keyword: val })), 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetKeyword.cancel();
    };
  }, [debouncedSetKeyword]);

  const handleSearchInputChange = (val) => {
    setKeyword(val);
    debouncedSetKeyword(val);
  };

  return (
    <Card>
      <Grid>
        {!hideKeywordSearch && (
          <Grid.col colSpan={3}>
            <Label>
              <FormattedMessage id="Global.KeywordSearch.Label" />
            </Label>
            <TextInput
              name="keyword"
              value={keyword}
              placeholder="Search by keyword"
              maxLength={50}
              onChange={(_, val) => handleSearchInputChange(val)}
              disabled={loading}
            />
          </Grid.col>
        )}
        {!hideSorts && (
          <>
            <Grid.col colSpan={3}>
              <Label>
                <FormattedMessage id="Global.SortBy" />
              </Label>
              <Select
                name="sortKey"
                value={selected?.sortKey}
                options={TIMELINE_LIST_SORTING_OPTIONS}
                placeholder="Sort by"
                onChange={(name, val) => setSelected((prev) => ({ ...prev, [name]: val }))}
                isClearable={false}
                disabled={loading}
              />
            </Grid.col>
            <Grid.col colSpan={3}>
              <Label>
                <FormattedMessage id="Global.SortDirection" />
              </Label>
              <Select
                name="sortDirection"
                value={selected?.sortDirection}
                options={Object.entries(DIRECTION).map(([label, value]) => ({ label: toUcFirst(label), value }))}
                onChange={(name, val) => setSelected((prev) => ({ ...prev, [name]: val }))}
                isClearable={false}
                disabled={loading}
              />
            </Grid.col>
          </>
        )}
      </Grid>
      {!hideAdvancedFilters && (
        <AdvancedFilters
          selected={selected}
          setSelected={setSelected}
          filterConfigMap={TIMELINE_LIST_FILTERS_CONFIG}
          loading={loading}
          {...props}
        />
      )}
    </Card>
  );
};

TimelineListControls.propTypes = {
  hideAdvancedFilters: PropTypes.bool,
  hideKeywordSearch: PropTypes.bool,
  hideSorts: PropTypes.bool,
  selected: PropTypes.object.isRequired,
  setSelected: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

export default TimelineListControls;
